<template>
  <div class="proscan-file-viewer">
    <div class="documents-panel">
      <p class="panel-title">View Documents</p>
      <p class="panel-subtitle" v-if="transactionNumber">Documents for #{{ transactionNumber }}</p>
      <el-table
        :data="files"
        empty-text="No documents"
        :highlight-current-row="true"
        @current-change="selectFile"
        style="width: 100%;"
        size="small">
        <el-table-column label="File" :show-overflow-tooltip="true">
          <template slot-scope="scope">
            <a href="javascript:void(0)" @click="selectFile(scope.row)">
              <i class="el-icon-document"></i> {{ scope.row.name }}
            </a>
          </template>
        </el-table-column>
        <el-table-column prop="uploadedOn" label="Date Uploaded" width="140" align="left" />
      </el-table>
    </div>
    <div class="preview-header" v-if="selectedFile">
      <span class="file-name" :title="selectedFile.name">
        <i class="el-icon-document"></i> {{ selectedFile.name }}
      </span>
      <a v-if="previewUrl" :href="previewUrl" target="_blank" class="open-link">Open in new tab</a>
    </div>
    <div class="preview-body" v-loading.body="loadingPreview" element-loading-text="Loading document...">
      <iframe v-if="previewUrl && isPdf" :src="pdfFrameUrl" class="preview-frame" frameborder="0"></iframe>
      <div v-else-if="previewUrl && isImage" class="preview-image">
        <img :src="previewUrl" :alt="selectedFile.name" />
      </div>
      <div v-else-if="previewUrl" class="preview-empty">
        This file type can't be previewed. Use the link above to open it.
      </div>
      <div v-else-if="!loadingPreview" class="preview-empty">
        {{ files.length ? 'Select a document to preview it.' : 'No documents on this transaction.' }}
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'ProscanFileViewer',
    props: {
      // Documents on this transaction: [{ id, name, uploadedOn }]
      files: {
        type: Array,
        'default': () => []
      },
      // Transaction number, shown above the document list
      transactionNumber: [String, Number],
      // Document to preselect, e.g. the one an extraction was run against
      primaryFileId: String
    },
    data () {
      return {
        selectedFile: null,
        previewUrl: null,
        loadingPreview: false
      }
    },
    computed: {
      /**
       * File extension of the selected document, used to pick a renderer
       * @return {string}
       */
      extension () {
        const name = this.selectedFile && this.selectedFile.name ? this.selectedFile.name : ''
        const parts = name.split('.')
        return parts.length > 1 ? parts.pop().toLowerCase() : ''
      },
      isPdf () {
        return this.extension === 'pdf'
      },
      isImage () {
        return ['jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff', 'bmp'].includes(this.extension)
      },
      /**
       * Preview url with the built in PDF viewer's toolbar, sidebar and scrollbar switched
       * off, so the pane shows the document only
       * @return {string}
       */
      pdfFrameUrl () {
        return this.previewUrl ? `${this.previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : ''
      }
    },
    methods: {
      /**
       * Selects a document and resolves its signed url for preview
       * @param file
       * @return {Promise<void>}
       */
      async selectFile (file) {
        if (!file || (this.selectedFile && this.selectedFile.id === file.id)) return
        this.selectedFile = file
        this.previewUrl = null
        this.loadingPreview = true
        try {
          this.previewUrl = await this.$store.dispatch('fetchFileDownloadUrl', file.id)
        } finally {
          this.loadingPreview = false
        }
      },
      /**
       * Preselects the extracted document, or the first one on the transaction
       */
      selectInitialFile () {
        if (!this.files.length) {
          this.selectedFile = null
          this.previewUrl = null
          return
        }
        const primary = this.files.find(row => row.id === this.primaryFileId) || this.files[0]
        const stillListed = this.selectedFile && this.files.some(row => row.id === this.selectedFile.id)
        if (!stillListed) this.selectFile(primary)
      }
    },
    watch: {
      files () {
        this.selectInitialFile()
      }
    },
    mounted () {
      this.selectInitialFile()
    }
  }
</script>

<style scoped>
  .proscan-file-viewer {
    display: flex;
    flex-direction: column;
    height: 100%;
    text-align: left;
  }
  .documents-panel {
    border: 1px solid #dfe6ec;
    border-bottom: none;
    border-radius: 3px 3px 0 0;
    padding: 10px 12px;
    background: #fff;
  }
  .panel-title {
    margin: 0;
    font-size: 15px;
    color: #1f2d3d;
  }
  .panel-subtitle {
    margin: 2px 0 8px;
    font-size: 12px;
    color: #8492a6;
  }
  .preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border: 1px solid #dfe6ec;
    background: #f5f7fa;
  }
  .file-name {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .open-link {
    font-size: 12px;
    margin-left: 10px;
    white-space: nowrap;
  }
  .preview-body {
    flex: 1;
    min-height: 0;
    background: #475669;
    border: 1px solid #dfe6ec;
    border-top: none;
    border-radius: 0 0 3px 3px;
  }
  .preview-frame {
    width: 100%;
    height: 100%;
  }
  .preview-image {
    height: 100%;
    overflow: auto;
    text-align: center;
    padding: 12px;
  }
  .preview-image img {
    max-width: 100%;
    background: #fff;
  }
  .preview-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 0;
    color: #d3dce6;
    padding: 20px;
    text-align: center;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Proscan/ProscanFileViewer.vue